import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SearchX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { AgentCard, WorkflowCard } from "@/components/catalogue/Cards";
import { useCatalogue } from "@/context/CatalogueContext";
import { shapesOf, workflowsForAgent } from "@/lib/catalogue";

const haystack = (a) => [
  a.chosen_name, a.working_name, ...(a.name_options || []), a.function, a.description,
  ...(Array.isArray(a.use_cases) ? a.use_cases : [a.use_cases]), ...(a.form_fields || []).map((f) => f.name),
].filter(Boolean).join(" ").toLowerCase();

const Filter = ({ id, label, value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-full rounded-xl bg-card sm:w-[180px]" aria-label={label} data-testid={`filter-${id}`}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-80 rounded-xl">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function FindAgent() {
  const { agents, workflows, teams, teamsById, loading, error, reload } = useCatalogue();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [team, setTeam] = useState("all");
  const [shape, setShape] = useState("all");
  const [status, setStatus] = useState("all");
  const [wf, setWf] = useState("all");
  const [kind, setKind] = useState("all");

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return agents.filter((a) => !a.parent)
      .filter((a) => !needle || haystack(a).includes(needle))
      .filter((a) => team === "all" || a.team === team)
      .filter((a) => shape === "all" || shapesOf(a).includes(shape))
      .filter((a) => status === "all" || (status === "live" ? a.live : !a.live))
      .filter((a) => wf === "all" || workflowsForAgent(a, workflows).some((w) => w.id === wf))
      .sort((x, y) => (teamsById[x.team]?.order ?? 99) - (teamsById[y.team]?.order ?? 99) || (x.display_order ?? 0) - (y.display_order ?? 0));
  }, [agents, workflows, teamsById, q, team, shape, status, wf]);

  const flows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return workflows.filter((w) => (!needle || `${w.name} ${w.one_line} ${w.key_benefit || ""} ${w.use_me_for || ""} ${w.id}`.toLowerCase().includes(needle))
      && (team === "all" || w.team === team) && (status === "all" || (status === "live" ? w.live : !w.live)) && (wf === "all" || w.id === wf));
  }, [workflows, q, team, status, wf]);

  return (
    <div data-testid="find-agent-page">
      <PageHeader title="Find an agent" description="Search by name, job, use case or form field, then narrow with the filters." />
      <div role="radiogroup" aria-label="Show" className="mb-4 inline-flex rounded-xl bg-muted p-1" data-testid="find-kind-toggle">
        {[["all", "All"], ["agents", "Agents"], ["workflows", "Workflows"]].map(([v, l]) => (
          <button key={v} type="button" role="radio" aria-checked={kind === v} onClick={() => setKind(v)} data-testid={`find-kind-${v}`}
            className={`rounded-lg px-4 py-1.5 text-sm transition-quick ${kind === v ? "bg-card font-medium shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>
      <div className="mb-8 flex flex-col gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 !h-4 !w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={q} onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })} className="pl-9" placeholder="Search the catalogue" aria-label="Search the catalogue" data-testid="find-agent-search" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
          <Filter id="team" label="Team" value={team} onChange={setTeam} options={[["all", "All teams"], ...teams.map((t) => [t.id, t.section || t.name])]} />
          <Filter id="shape" label="Shape" value={shape} onChange={setShape} options={[["all", "Any shape"], ["chatbot", "Chat"], ["form", "Form"], ["scheduled", "Scheduled"]]} />
          <Filter id="status" label="Status" value={status} onChange={setStatus} options={[["all", "Live and coming soon"], ["live", "Live"], ["soon", "Coming soon"]]} />
          <Filter id="workflow" label="Workflow" value={wf} onChange={setWf} options={[["all", "Any workflow"], ...workflows.map((w) => [w.id, `${w.id} · ${w.name}`])]} />
        </div>
      </div>
      {error ? <ErrorState message={error} onRetry={reload} /> : loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
      ) : (
        <>
          {(() => {
            const cards = [...(kind !== "workflows" ? results.map((a) => ({ k: `a-${a.emp_no}`, el: <AgentCard key={a.emp_no} agent={a} /> })) : []),
              ...(kind !== "agents" ? flows.map((w) => ({ k: `w-${w.id}`, el: <WorkflowCard key={w.id} workflow={w} /> })) : [])];
            const na = kind !== "workflows" ? results.length : 0;
            const nw = kind !== "agents" ? flows.length : 0;
            return (
              <>
                <p className="mb-4 text-sm text-muted-foreground" data-testid="find-agent-count">{na} {na === 1 ? "agent" : "agents"}{kind === "all" || kind === "workflows" ? `, ${nw} ${nw === 1 ? "workflow" : "workflows"}` : ""}</p>
                {cards.length === 0 ? <EmptyState icon={SearchX} title="Nothing matches" body="Try fewer words or clear a filter." action={<button type="button" className="text-sm font-medium underline underline-offset-4" onClick={() => { setParams({}); setTeam("all"); setShape("all"); setStatus("all"); setWf("all"); }} data-testid="find-clear">Clear search and filters</button>} testid="find-agent-empty" />
                  : <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="agent-grid">{cards.map((c) => c.el)}</div>}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
